import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Stack,
  Typography,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  PhotoCamera as PhotoCameraIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { makeApiCall } from '../../config/api';

/**
 * Photo capture + gallery for an active job.
 *
 * Legacy parity: TakePhoto / JobSiteImageCapture from the summer, winter
 * and construction on-site logs. Used in two places:
 *  - CrewToday "Services & Logs" dialog (mobile camera capture)
 *  - JobDetailView "Photos" tab (office upload/review)
 *
 * Photos are stored in S3 via POST /api/jobs/:id/photos and surface on the
 * customer's Property Live Update email + public status page.
 *
 * Props:
 *  - jobId: active job id (required)
 *  - canDelete: show delete buttons (uploader can always delete own photos
 *    server-side; this just controls UI affordance)
 *  - onChange: optional callback fired after upload/delete succeeds
 */
const JobPhotosSection = ({ jobId, canDelete = false, onChange }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const fileInputRef = useRef(null);

  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [viewerPhoto, setViewerPhoto] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadPhotos = useCallback(async () => {
    if (!jobId) return;
    try {
      const res = await makeApiCall(`api/jobs/${jobId}/photos`);
      if (!res.ok) throw new Error();
      const data = await res.json();
      setPhotos(data.photos || []);
    } catch (_) {
      // Non-fatal: keep whatever we had; surface a soft error only on
      // explicit user actions, not background loads.
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    setLoading(true);
    loadPhotos();
  }, [loadPhotos]);

  const handleFilesSelected = async (event) => {
    const files = Array.from(event.target.files || []);
    // Reset so re-selecting the same file fires onChange again.
    event.target.value = '';
    if (files.length === 0 || !jobId) return;

    setError('');
    setUploading(true);
    try {
      const formData = new FormData();
      files.forEach((file) => formData.append('photos', file));
      const res = await makeApiCall(`api/jobs/${jobId}/photos`, {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || t('jobs.photoUploadFailed'));
      }
      await loadPhotos();
      if (onChange) onChange();
    } catch (err) {
      setError(err.message || t('jobs.photoUploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setError('');
    try {
      const res = await makeApiCall(`api/jobs/${jobId}/photos/${deleteTarget.id}`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || t('jobs.photoDeleteFailed'));
      }
      setDeleteTarget(null);
      await loadPhotos();
      if (onChange) onChange();
    } catch (err) {
      setError(err.message || t('jobs.photoDeleteFailed'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <PhotoCameraIcon color="action" />
        <Typography variant="h6" component="h3" sx={{ m: 0, flexGrow: 1 }}>
          {t('jobs.photos')}
        </Typography>
        <Button
          variant="contained"
          size={isSmall ? 'medium' : 'small'}
          startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <PhotoCameraIcon />}
          onClick={() => fileInputRef.current && fileInputRef.current.click()}
          disabled={uploading || !jobId}
          aria-label={t('jobs.addPhotos')}
        >
          {uploading ? t('jobs.uploadingPhotos') : t('jobs.addPhotos')}
        </Button>
      </Stack>

      {/* Hidden input: capture="environment" opens the rear camera directly
          on phones/tablets; on desktop it falls back to a file picker. */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        hidden
        onChange={handleFilesSelected}
        aria-hidden="true"
      />

      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {!loading && photos.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {t('jobs.noPhotos')}
        </Typography>
      )}

      {!loading && photos.length > 0 && (
        <ImageList cols={isSmall ? 2 : 4} gap={8} sx={{ m: 0 }}>
          {photos.map((photo) => (
            <ImageListItem key={photo.id}>
              <Box
                component="img"
                src={photo.signed_url}
                alt={photo.caption || photo.original_filename || t('jobs.photos')}
                loading="lazy"
                onClick={() => setViewerPhoto(photo)}
                sx={{
                  width: '100%',
                  height: 120,
                  objectFit: 'cover',
                  borderRadius: 1,
                  cursor: 'pointer',
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') setViewerPhoto(photo);
                }}
                aria-label={t('jobs.viewPhotoAria', {
                  name: photo.original_filename || '',
                })}
              />
              {canDelete && (
                <ImageListItemBar
                  position="top"
                  sx={{ background: 'transparent' }}
                  actionIcon={
                    <IconButton
                      size="small"
                      onClick={() => setDeleteTarget(photo)}
                      aria-label={t('jobs.deletePhoto')}
                      sx={{
                        color: '#fff',
                        backgroundColor: 'rgba(0,0,0,0.45)',
                        m: 0.5,
                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.65)' },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  }
                />
              )}
            </ImageListItem>
          ))}
        </ImageList>
      )}

      {/* Full-size viewer */}
      <Dialog
        open={!!viewerPhoto}
        onClose={() => setViewerPhoto(null)}
        maxWidth="lg"
        fullWidth
        aria-labelledby="job-photo-viewer-title"
      >
        <DialogTitle id="job-photo-viewer-title" sx={{ pr: 6 }}>
          {viewerPhoto?.original_filename || t('jobs.photos')}
          <IconButton
            aria-label={t('common.close')}
            onClick={() => setViewerPhoto(null)}
            sx={{ position: 'absolute', top: 8, right: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
          {viewerPhoto && (
            <Box
              component="img"
              src={viewerPhoto.signed_url}
              alt={viewerPhoto.caption || viewerPhoto.original_filename || ''}
              sx={{ maxWidth: '100%', maxHeight: '78vh', objectFit: 'contain' }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => !deleting && setDeleteTarget(null)}
        aria-labelledby="job-photo-delete-title"
      >
        <DialogTitle id="job-photo-delete-title">{t('jobs.deletePhoto')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{t('jobs.deletePhotoConfirm')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : <DeleteIcon />}
            aria-label={t('jobs.deletePhoto')}
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default JobPhotosSection;
